import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import PersonaSelector from '@/screens/PersonaSelector';
import NetworkView from '@/screens/NetworkView';
import StudioView from '@/screens/StudioView';
import OperatorQueue from '@/screens/OperatorQueue';
import CustomerRecord from '@/screens/CustomerRecord';
import CallingWorkspace from '@/screens/CallingWorkspace';
import LiveAIConversations from '@/screens/LiveAIConversations';
import Conversations from '@/screens/Conversations';
import ReactivationEngine from '@/screens/ReactivationEngine';
import Nurture from '@/screens/Nurture';
import StudioCalendar from '@/screens/StudioCalendar';
import SelfBookLinkGenerator from '@/screens/SelfBookLinkGenerator';
import InChairRebooking from '@/screens/InChairRebooking';
import DepositCollection from '@/screens/DepositCollection';
import ReportingDashboard from '@/screens/ReportingDashboard';
import ReportingConcept from '@/screens/ReportingConcept';
import InternalComms from '@/screens/InternalComms';
import FutureReadinessCalendar from '@/screens/FutureReadinessCalendar';
import Settings from '@/screens/Settings';
import ArtistDashboard from '@/screens/ArtistDashboard';
import ApprenticeDashboard from '@/screens/ApprenticeDashboard';
import OwnerDashboard from '@/screens/OwnerDashboard';
import Leaderboard from '@/screens/Leaderboard';
import LeaderboardTV from '@/screens/LeaderboardTV';
import PlaytestConsole from '@/screens/PlaytestConsole';
import ControlConsole from '@/screens/ControlConsole';
import ConfirmationCalls from '@/screens/ConfirmationCalls';
import ArtistAvailability from '@/screens/ArtistAvailability';
import DepositsLedger from '@/screens/DepositsLedger';
import ArtistManagement from '@/screens/ArtistManagement';
import FloorManager from '@/screens/FloorManager';
import FloorBooking from '@/screens/FloorBooking';
import PayoutsCash from '@/screens/PayoutsCash';
import Reconciliation from '@/screens/Reconciliation';
import Calculations from '@/screens/Calculations';
import Customers from '@/screens/Customers';
import DetailShowcase from '@/screens/DetailShowcase';
import HowItWorks from '@/screens/HowItWorks';
import SelfBookPage from '@/screens/customer-flow/SelfBookPage';
import TopupPayPage from '@/screens/customer-flow/TopupPayPage';
import WaiverSignPage from '@/screens/customer-flow/WaiverSignPage';
import AppShell from '@/app/AppShell';
import Sweeper from '@/screens/Sweeper';
import OpsWorklist from '@/screens/OpsWorklist';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { startSimulation, stopSimulation } from '@/events/simulationEngine';
import { canAccessFinanceScreens } from '@/domain/permissions';

export default function App() {
  const initialised = useBrainStore((s) => s.initialised);
  const loadFixtures = useBrainStore((s) => s.loadFixtures);
  const persona = useSessionStore((s) => s.persona);

  useEffect(() => {
    if (!initialised) {
      loadFixtures();
    }
  }, [initialised, loadFixtures]);

  useEffect(() => {
    if (!initialised) return;
    startSimulation();
    return () => stopSimulation();
  }, [initialised]);

  // Expose test-only window APIs in dev builds.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as typeof window & {
      __sim?: unknown;
      __brain?: unknown;
      __session?: unknown;
      __ui?: unknown;
    };
    import('@/events/simulationEngine').then((mod) => {
      w.__sim = mod.simulationApi;
    });
    w.__brain = useBrainStore;
    w.__session = useSessionStore;
    import('@/state/uiStore').then((mod) => {
      w.__ui = mod.useUIStore;
    });
    import('@/sound/audioSystem').then((mod) => {
      (w as typeof w & { __getAudioCalls?: () => string[] }).__getAudioCalls =
        mod.getAudioCalls;
      (w as typeof w & { __resetAudioCalls?: () => void }).__resetAudioCalls =
        mod.resetAudioCalls;
    });
  }, []);

  // Route persona-less users to the splash; everything else mounts under AppShell.
  return (
    <Routes>
      <Route path="/" element={<PersonaSelector />} />
      <Route path="/customer-book/:linkToken" element={<SelfBookPage />} />
      <Route path="/customer-topup/:planId" element={<TopupPayPage />} />
      <Route path="/customer-waiver/:waiverId" element={<WaiverSignPage />} />
      <Route
        path="/leaderboard/tv/:studioId"
        element={persona ? <LeaderboardTV /> : <Navigate to="/" />}
      />
      <Route path="/console" element={<ControlConsole />} />
      <Route element={<AppShell />}>
        <Route path="/network" element={persona ? <NetworkView /> : <Navigate to="/" />} />
        <Route path="/studio/:studioId" element={persona ? <StudioView /> : <Navigate to="/" />} />
        <Route path="/queue" element={persona ? <OperatorQueue /> : <Navigate to="/" />} />
        <Route path="/sweeper" element={persona === 'georgina' ? <Sweeper /> : <Navigate to="/" />} />
        <Route path="/ops" element={persona === 'georgina' ? <OpsWorklist /> : <Navigate to="/" />} />
        <Route
          path="/customer/:customerId"
          element={persona ? <CustomerRecord /> : <Navigate to="/" />}
        />
        <Route
          path="/conversations"
          element={persona ? <Conversations /> : <Navigate to="/" />}
        />
        {/* Legacy routes — kept alive so existing links + the Simulate Inbound
            tooling on /calling still work. Surfaced as filter-pre-applied views
            of the new Conversations inbox. */}
        <Route path="/calling" element={persona ? <CallingWorkspace /> : <Navigate to="/" />} />
        <Route
          path="/ai-conversations"
          element={persona ? <LiveAIConversations /> : <Navigate to="/" />}
        />
        <Route
          path="/reactivation"
          element={persona ? <ReactivationEngine /> : <Navigate to="/" />}
        />
        <Route path="/nurture" element={persona ? <Nurture /> : <Navigate to="/" />} />
        <Route
          path="/calendar/:studioId"
          element={persona ? <StudioCalendar /> : <Navigate to="/" />}
        />
        <Route
          path="/self-book-generator"
          element={persona ? <SelfBookLinkGenerator /> : <Navigate to="/" />}
        />
        <Route
          path="/in-chair-rebooking/:bookingId"
          element={persona ? <InChairRebooking /> : <Navigate to="/" />}
        />
        <Route
          path="/deposit/:depositId"
          element={persona ? <DepositCollection /> : <Navigate to="/" />}
        />
        <Route path="/deposits" element={persona ? <DepositsLedger /> : <Navigate to="/" />} />
        {/* /approvals retired (tv-i0b): the no-deposit override queue now lives on
            the Ops worklist Overrides tab. Keep the path so console + deep-links
            do not 404 - redirect to /ops. */}
        <Route path="/approvals" element={<Navigate to="/ops" replace />} />
        <Route path="/artists" element={persona ? <ArtistManagement /> : <Navigate to="/" />} />
        <Route path="/floor" element={persona ? <FloorManager /> : <Navigate to="/" />} />
        <Route path="/floor/:bookingId" element={persona ? <FloorBooking /> : <Navigate to="/" />} />
        <Route path="/payouts" element={persona ? <PayoutsCash /> : <Navigate to="/" />} />
        <Route
          path="/reconciliation"
          element={
            persona && canAccessFinanceScreens(persona) ? (
              <Reconciliation />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/calculations"
          element={
            persona && canAccessFinanceScreens(persona) ? (
              <Calculations />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        {/* Settlement folded into Reconcile (amendment 11); keep the old path as a redirect. */}
        <Route path="/settlement" element={<Navigate to="/reconciliation" replace />} />
        <Route path="/reporting" element={persona ? <ReportingDashboard /> : <Navigate to="/" />} />
        <Route
          path="/reporting-concept"
          element={persona ? <ReportingConcept /> : <Navigate to="/" />}
        />
        <Route path="/comms" element={persona ? <InternalComms /> : <Navigate to="/" />} />
        <Route
          path="/future-readiness"
          element={persona ? <FutureReadinessCalendar /> : <Navigate to="/" />}
        />
        <Route path="/settings" element={persona ? <Settings /> : <Navigate to="/" />} />
        <Route
          path="/confirmations"
          element={persona ? <ConfirmationCalls /> : <Navigate to="/" />}
        />
        <Route
          path="/availability"
          element={persona ? <ArtistAvailability /> : <Navigate to="/" />}
        />
        <Route path="/customers" element={persona ? <Customers /> : <Navigate to="/" />} />
        <Route path="/artist/:artistId" element={persona ? <ArtistDashboard /> : <Navigate to="/" />} />
        <Route
          path="/apprentice/:apprenticeId"
          element={persona ? <ApprenticeDashboard /> : <Navigate to="/" />}
        />
        <Route path="/owner" element={persona ? <OwnerDashboard /> : <Navigate to="/" />} />
        <Route
          path="/leaderboard"
          element={persona ? <Leaderboard /> : <Navigate to="/" />}
        />
        <Route
          path="/playtest"
          element={persona ? <PlaytestConsole /> : <Navigate to="/" />}
        />
        <Route path="/showcase" element={persona ? <DetailShowcase /> : <Navigate to="/" />} />
        <Route path="/how-it-works" element={persona ? <HowItWorks /> : <Navigate to="/" />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Suppress unused warning for the simulation store; consumed via window.__sim.
void useSimulationStore;
